import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import TaskRouteMap from "@/components/TaskRouteMap";

const ORD = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
const fmt = (iso, a, b) => {
  if (!iso) return "—";
  const d = new Date(iso + "T00:00:00");
  return `${ORD(d.getDate())} ${d.toLocaleString("en-GB", { month: "short" })} ${d.getFullYear()} ${a || ""} - ${b || ""}`;
};

export default function ShiftPrint() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [route, setRoute] = useState(null);
  const [routeDone, setRouteDone] = useState(false);
  const printed = useRef(false);

  useEffect(() => {
    api.get(`/scheduler/shifts/${id}`).then(async (r) => {
      setData(r.data);
      const taskId = r.data?.shift?.task_legacy_id;
      if (taskId) {
        try { const rt = await api.get(`/tasks/${taskId}/route`); setRoute(rt.data); } catch { /* no route */ }
      }
      setRouteDone(true);
    }).catch(() => setRouteDone(true));
  }, [id]);

  // Print once data + (map tiles) settle.
  useEffect(() => {
    if (!data || !routeDone || printed.current) return;
    printed.current = true;
    const hasMap = route && route.available !== false;
    const delay = hasMap ? 2200 : 500;
    const t = setTimeout(() => window.print(), delay);
    return () => clearTimeout(t);
  }, [data, routeDone, route]);

  if (!data) return <div style={{ padding: 40, fontFamily: "Arial" }}>Preparing shift print view…</div>;

  const s = data.shift;
  const req = data.requirements || {};
  const steps = data.day_timeline || [];
  const resources = data.resources || [];
  const showMap = route && route.available !== false;

  const row = (label, value) => (
    <tr>
      <td style={{ padding: "6px 10px", color: "#64748b", border: "1px solid #e2e8f0", width: 200 }}>{label}</td>
      <td style={{ padding: "6px 10px", color: "#0f172a", border: "1px solid #e2e8f0", fontWeight: 600 }}>{value || "—"}</td>
    </tr>
  );

  const ReqSection = ({ title, sec }) => {
    if (!sec) return null;
    return (
      <div style={{ marginBottom: 14 }} data-testid={`print-req-${title.replace(/\s+/g, "-").toLowerCase()}`}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <strong style={{ fontSize: 13 }}>{title}</strong>
          <span style={{ fontSize: 11, color: sec.complete ? "#059669" : "#64748b" }}>{sec.status}</span>
        </div>
        <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 13 }}>
          {(sec.items || []).map((it) => (
            <li key={it.label} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", borderBottom: "1px solid #f1f5f9" }}>
              <span>{it.done ? "☑" : "☐"} {it.label}</span>
              <span style={{ color: "#94a3b8", fontSize: 11 }}>{it.done ? `Completed by ${it.completed_by} at ${it.completed_at}` : "Pending"}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: 32, fontFamily: "Arial, sans-serif", color: "#0f172a" }} data-testid="shift-print">
      <style>{`@media print { .no-print { display:none !important; } @page { margin: 14mm; } }`}</style>
      <div className="no-print" style={{ marginBottom: 16 }}>
        <button onClick={() => window.print()} data-testid="shift-print-now-btn"
          style={{ background: "#4F46E5", color: "#fff", border: 0, padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 13 }}>
          Print / Save as PDF
        </button>
      </div>

      <div style={{ fontSize: 11, letterSpacing: 1, textTransform: "uppercase", color: "#94a3b8" }}>HTSS Ops · Shift Summary</div>
      <h1 style={{ margin: "4px 0 2px", fontSize: 22 }}>Shift {s.display_no || s.shift_id} — {s.work_type || "Shift"}</h1>
      <div style={{ color: "#64748b", fontSize: 13, marginBottom: 18 }}>
        {s.location || "—"}{s.task_legacy_id ? ` · Task ${s.task_legacy_id}` : ""}
      </div>

      <h2 style={{ fontSize: 15, color: "#4F46E5", margin: "18px 0 6px" }}>Details</h2>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13 }}>
        <tbody>
          {row("Task ID", s.task_legacy_id)}
          {row("Shift ID", s.display_no || s.shift_id)}
          {row("Time", fmt(s.date, s.start_time, s.end_time))}
          {row("Logged By", s.logged_by)}
          {row("Location", s.location)}
          {row("Route", `${s.route?.miles || 0} miles, ${s.route?.mins || 0} mins`)}
          {row("Status", s.shift_status)}
          {row("Resources", resources.map((r) => r.name).join(", "))}
          {s.weather && row("Weather", `${s.weather.label} · ${s.weather.temp}`)}
        </tbody>
      </table>

      {showMap && (
        <>
          <h2 style={{ fontSize: 15, color: "#4F46E5", margin: "18px 0 6px" }}>Route</h2>
          <TaskRouteMap route={route} className="w-full" />
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>
            {(route.route?.distance_miles ?? route.route?.distance ?? "—")} miles ·
            {" "}{(route.route?.duration_mins ?? route.route?.duration ?? "—")} mins driving
          </div>
        </>
      )}

      <h2 style={{ fontSize: 15, color: "#4F46E5", margin: "18px 0 6px" }}>Requirements</h2>
      <ReqSection title="On Site" sec={req.on_site} />
      <ReqSection title="Post Site Checks" sec={req.post_site} />

      <h2 style={{ fontSize: 15, color: "#4F46E5", margin: "18px 0 6px" }}>Day Summary</h2>
      <ul style={{ fontSize: 13, listStyle: "none", padding: 0, margin: 0 }}>
        {steps.map((st, i) => (
          <li key={i} style={{ padding: "3px 0" }}>
            {st.done ? "✓" : "•"} <strong>{st.label}</strong>{st.detail ? ` — ${st.detail}` : ""}
          </li>
        ))}
        {steps.length === 0 && <li>No shifts for this day.</li>}
      </ul>

      <p style={{ fontSize: 11, color: "#94a3b8", marginTop: 24 }}>Demonstration data only. Traffic overlay is illustrative.</p>
    </div>
  );
}
